import React, { useState } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import { showNotification } from "aldoo-ra/Notification"
import { url } from "aldoo-ra/CMS/url"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import { PlusCircleIcon, PencilIcon, TrashIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline"
import Tooltip from "aldoo-ra/Tooltip"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Badge from "aldoo-ra/Badge"
import ProductPurchaseFormatter from "aldoo-ra/CMSModels/ProductPurchase/formatters/product-purchase-formatter"
import ProductPurchaseRestoreDialog from "./RestoreDialog"
import StoreProductInfoDialog from "aldoo-ra/Admin/Store/Products/StoreProductInfoDialog"
import { api } from "aldoo-ra/API"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils.jsx"

import apiInterface from "aldoo-ra/CMSModels/ProductPurchase/api"
const { Get, Save, SoftDelete } = apiInterface

// Import user API for search
import UserPlatformAPI from "aldoo-ra/Admin/PlatformAccess/user-api"

export default function PurchasesBrowser() {
  const { getType } = DatabaseModelTypeResolver()
  const ProductPurchase = getType("ProductPurchase")

  const [selectedInstance, setSelectedInstance] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [statusFilter, setStatusFilter] = useState([])
  const [platformFilter, setPlatformFilter] = useState([])
  const [userFilter, setUserFilter] = useState(null)
  const [showRestoreDialog, setShowRestoreDialog] = useState(false)
  const [selectedProductPid, setSelectedProductPid] = useState(null)
  const [showProductInfoDialog, setShowProductInfoDialog] = useState(false)

  const getStatusFilter = () => {
    if (!statusFilter || !statusFilter.length) return { $exists: true }
    return { $in: statusFilter.map((t) => t.value) }
  }

  const getPlatformFilter = () => {
    if (!platformFilter || !platformFilter.length) return { $exists: true }
    return { $in: platformFilter.map((t) => t.value) }
  }

  const getUserFilter = () => {
    if (!userFilter || !userFilter.length) return { $exists: true }
    // userFilter is an array when using DropdownSelect, get the first selected user
    const selectedUser = userFilter[0]
    return selectedUser._id
  }

  const handleProductClick = (pid) => {
    setSelectedProductPid(pid)
    setShowProductInfoDialog(true)
  }

  // Custom API method that handles search with user population and product fetching
  const customGetMethod = async (requestParams) => {
    // Merge the filters with the request params
    const finalParams = {
      ...requestParams,
      populate: ["user"],
      match: {
        ...requestParams.match,
        status: getStatusFilter(),
        platform: getPlatformFilter(),
        user: getUserFilter(),
        deleted: { $ne: true }
      }
    }

    const response = await Get(finalParams)

    // Batch fetch products for all purchases
    if (response && response.data && response.data.length > 0) {
      const uniquePids = [...new Set(response.data.map(purchase => purchase.pid).filter(Boolean))]

      if (uniquePids.length > 0) {
        try {
          const crudAPI = api("crud")
          const productsResponse = await crudAPI({
            get: {
              model: "StoreProduct",
              match: { pid: { $in: uniquePids } }
            }
          })

          const productsMap = {}
          if (Array.isArray(productsResponse)) {
            productsResponse.forEach(product => {
              productsMap[product.pid] = product
            })
          }

          response.data = response.data.map(purchase => ({
            ...purchase,
            product: productsMap[purchase.pid] || null
          }))
        } catch (error) {
          console.error("Failed to fetch products:", error)
        }
      }
    }

    return response
  }

  const handleItemDelete = (item) => {
    showPromptDialog({
      title: `Confirm Delete`,
      message: `Are you sure you want to delete purchase for ${item.pid}?`,
      confirmText: "Delete",
      onConfirm: async () => {
        const response = await SoftDelete(item)

        if (response.error) {
          showNotification({
            message: response.error,
            className: "bg-admin_error text-white",
          })
          return
        } else {
          showNotification({
            message: `Purchase deleted successfully`,
            className: "bg-admin_primary text-admin_text_inv",
          })
          setRefreshKey((prev) => prev + 1)
        }
      },
    })
  }

  const handleItemSave = async (item) => {
    const response = await Save(item)

    if (response.error) {
      showNotification({
        message: response.error,
        className: "bg-admin_error text-white",
      })
      return
    } else {
      showNotification({
        message: `Purchase ${!item._id ? "created" : "updated"} successfully`,
        className: "bg-admin_primary text-admin_text_inv",
      })
      setRefreshKey((prev) => prev + 1)
    }

    return item
  }


  const formatDate = (date) => {
    if (!date) return "N/A"
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "active": return "success"
      case "refunded": return "warning"
      case "revoked": return "error"
      default: return "default"
    }
  }

  const purchaseItemRender = [
    {
      id: "price",
      title: "Price",
      headerClassName: "w-[80px]",
      render: (value, item) => {
        const priceModel = Array.isArray(item.price_model)
          ? item.price_model[0]
          : item.price_model;

        if (!priceModel || !priceModel.amount) {
          return (
            <Typography
              variant="admin_txt1"
              className="text-admin_text_secondary"
            >
              N/A
            </Typography>
          );
        }

        return (
          <div className="flex flex-col">
            <Typography
              variant="admin_txt1"
              className="font-semibold text-admin_primary"
            >
              {priceModel.currency || 'USD'} {parseFloat(priceModel.amount).toFixed(2)}
            </Typography>
          </div>
        );
      },
    },
    {
      id: "product",
      title: "Product",
      headerClassName: "w-[120px] xl:w-[200px]",
      render: (value, item) => (
        <button
          onClick={() => handleProductClick(item.pid)}
          className="text-admin_primary hover:text-admin_primary/80 font-semibold text-left transition-colors"
        >
          {item.product?.name || item.pid}
        </button>
      ),
    },
    {
      id: "user",
      title: "User",
      headerClassName: "w-[120px] xl:w-1/4",
      render: (value, item) => (
        <div className="flex flex-col w-full">
          <div className="min-w-0 flex-1">
            <Typography
              variant="admin_txt1"
              className="block font-semibold whitespace-nowrap truncate my-0"
            >
              {item.user?.name || "Unknown User"}
            </Typography>

            <Tooltip
              display="block"
              content={
                <div className="text-center">
                  <Typography
                    variant="admin_txt1"
                    className="block"
                  >
                    {item.user?.email || "No email"}
                  </Typography>

                  <Typography variant="admin_txt2_dim">
                    Click to copy
                  </Typography>
                </div>
              }
            >
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(item.user?.email)
                    showNotification({
                      message: "Copied to clipboard",
                      className: "bg-admin_primary text-admin_text_inv"
                    })
                  } catch (error) {
                    console.error("Failed to copy:", error)
                  }
                }}
                className="w-full truncate whitespace-nowrap text-admin_text_secondary hover:text-admin_primary transition-colors text-left"
              >
                {item.user?.email || "No email"}
              </button>
            </Tooltip>
            </div>
        </div>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[40px] xl:w-[70px]",
      render: (value, item) => (
        <Badge variant={getStatusBadgeVariant(item.status)}>
          {item.status}
        </Badge>
      ),
    },
    {
      id: "platform",
      title: "Platform",
      headerClassName: "w-[80px] text-center",
      render: (value, item) => (
        <Tooltip
          content={getPlatformDisplayName(item.platform)}
          display="block"
        >
          <div className="flex items-center justify-center">
            {renderPlatformIcon(item.platform, "w-6 h-6 text-admin_text_secondary")}
          </div>
        </Tooltip>
      ),
    },
    {
      id: "purchase_date",
      title: "Purchase Date",
      headerClassName: "w-[90px] xl:w-[140px]",
      render: (value, item) => (
        <div className="flex flex-col">
          <span>
            {formatDate(item.purchase_date)}
          </span>
          {item.created && item.created !== item.purchase_date && (
            <span className="text-xs text-admin_text_secondary">
              Created: {formatDate(item.created)}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[60px] xl:w-[70px] text-center",
      render: (value, item) => (
        <div className="flex items-center justify-center">
          <Button
            variant="admin_icon"
            className="text-admin_text_secondary px-3"
            onClick={() => setSelectedInstance(item)}
          >
            <PencilIcon className="w-5 h-5" />
          </Button>

          <Button
            variant="admin_icon"
            className="text-admin_text_secondary hover:text-admin_error px-3"
            onClick={() => handleItemDelete(item)}
          >
            <TrashIcon className="w-5 h-5 items-center" />
          </Button>
        </div>
      ),
    },
  ]

  const headerControls = (
    <div className="w-full flex items-center gap-3 -mt-5">
        <DropDownSelect
          variant="admin"
          options={[
            { value: "active", name: "Active" },
            { value: "refunded", name: "Refunded" },
            { value: "revoked", name: "Revoked" },
          ]}
          multiSelection
          fixedWidth="160px"
          placeholder="Filter by status"
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value)
            setRefreshKey((prev) => prev + 1)
          }}
        />

        <DropDownSelect
          variant="admin"
          options={[
            { value: "Web", name: "Web" },
            { value: "iOS", name: "iOS" },
            { value: "Android", name: "Android" },
            { value: "Aldoo", name: "Aldoo" },
          ]}
          multiSelection
          fixedWidth="180px"
          placeholder="Filter by platform"
          value={platformFilter}
          onChange={(value) => {
            setPlatformFilter(value)
            setRefreshKey((prev) => prev + 1)
          }}
        />

        <DropDownSelect
          variant="admin"
          searchEnabled
          useExternalSearch
          enableResetButton
          showCheckbox={false}
          apiMethod={async (params) => {
            if (!params.searchTerm || params.searchTerm.length < 2) {
              return { data: [] }
            }

            const response = await UserPlatformAPI.SearchUsers(params.searchTerm)
            return response
          }}
          onDataLoaded={(rawData) => {
            // Transform raw user data into dropdown format
            return rawData.map(user => ({
              _id: user._id,
              name: user.name || 'Unknown User',
              email: user.email || 'No email',
              value: user._id,
              label: `${user.name || 'Unknown User'} (${user.email || 'No email'})`,
              ...user // Keep original user data
            }))
          }}
          searchField="name email"
          valueField="_id"
          nameField="label"
          fixedWidth="260px"
          placeholder="Filter by user"
          noDataMessage="Filter by user"
          value={userFilter}
          onChange={(value) => {
            setUserFilter(value)
            setRefreshKey((prev) => prev + 1)
          }}
          onValueItemRender={(user) => {
            if (!user) return "Filter by user"
            return (
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0" data-user-avatar={user._id || user.id}>
                  {user.avatar ? (
                    <img src={url(user.avatar)} alt={user.name} className="w-6 h-6 rounded-full object-cover" data-user-avatar={user._id || user.id} />
                  ) : (
                    <span className="text-white font-medium text-xs">
                      {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-admin_text truncate">
                    {user.name || 'Unknown User'}
                  </div>
                  <div className="text-xs text-admin_text_secondary truncate">
                    {user.email || 'No email'}
                  </div>
                </div>
              </div>
            )
          }}
          onItemRender={(user) => (
            <div className="flex items-center gap-3 p-2">
              {/* User Avatar */}
              <div className="w-8 h-8 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0" data-user-avatar={user._id || user.id}>
                {user.avatar ? (
                  <img src={url(user.avatar)} alt={user.name} className="w-8 h-8 rounded-full object-cover" data-user-avatar={user._id || user.id} />
                ) : (
                  <span className="text-white font-medium text-sm">
                    {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">
                  {user.name || 'Unknown User'}
                </div>
                <div className="text-xs text-gray-500">
                  {user.email || 'No email'}
                </div>
              </div>
            </div>
          )}
        />
    </div>
  )

  const validators = (errors) => ({
    pid: async (value, item) => {
      if (!value) {
        errors.pid = "Product ID is required"
        return
      }
      if (value.length < 3) {
        errors.pid = "Product ID must be at least 3 characters long"
      }
    },
    user: async (value, item) => {
      if (!value) {
        errors.user = "User is required"
      }
    },
    status: async (value, item) => {
      const validStatuses = ['active', 'refunded', 'revoked']
      if (!value || !validStatuses.includes(value)) {
        errors.status = "Invalid status"
      }
    },
  })

  return (
    <div className="p-4">
      {/* Tittle and Buttons */}
      <div className="w-full flex items-center justify-between mb-5">
        <Typography
          component="h1"
          variant="admin_h3"
          className="ml-4"
        >
          Product Purchases
        </Typography>

        <div className="flex gap-2 items-center">
          <Button
            variant="admin_secondary"
            onClick={() => setShowRestoreDialog(true)}
          >
            <div className="flex items-center gap-2">
              <ArrowUturnLeftIcon className="w-5 h-5" />
              Restore
            </div>
          </Button>

          <Button
            variant="admin_primary"
            onClick={() => setSelectedInstance({})}
          >
            <div className="flex items-center gap-2">
              <PlusCircleIcon className="w-5 h-5" />
              Add
            </div>
          </Button>
        </div>
      </div>

      <DataTable
        variant="admin_browser"
        preferredHeight="70vh"
        apiMethod={customGetMethod}
        headerControls={headerControls}
        itemHeight="h-[80px]"
        searchField="pid platform status"
        searchObject={{}}
        requestFields={{}}
        sort={{ purchase_date: -1 }}
        localUserPreferencesKey="admin.store.purchases"
        renders={purchaseItemRender}
        refreshKey={refreshKey}
        styleOverrides={{
          table: "w-full table-fixed",
          searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[250px] xl:w-[350px] h-[30px] px-2 py-1"
        }}
        noData={
          <Typography variant="admin_body">
            No purchases matching the search criteria
          </Typography>
        }
      />

      <Modal
        isOpen={selectedInstance !== null}
        onClose={() => setSelectedInstance(null)}
        variant="admin"
        className="w-full max-w-[900px]"
      >
        <ContentEditor
          title="Product Purchase"
          validators={validators}
          formatters={[ProductPurchaseFormatter]}
          contentType={ProductPurchase}
          fieldValues={selectedInstance || {}}
          onError={(errors) => {
            showNotification({
              message: Object.values(errors).find((e) => e),
              className: "bg-admin_error text-white",
            })
          }}
          onSave={handleItemSave}
          onClickSave={(result) => {
            if (!result) return
            setSelectedInstance(null)
          }}
        />
      </Modal>

      <ProductPurchaseRestoreDialog
        isOpen={showRestoreDialog}
        onClose={() => setShowRestoreDialog(false)}
        onRefresh={() => setRefreshKey((prev) => prev + 1)}
      />

      <StoreProductInfoDialog
        isOpen={showProductInfoDialog}
        onClose={() => setShowProductInfoDialog(false)}
        pid={selectedProductPid}
      />
    </div>
  )
}